import { Stack } from '@mui/material';

import { ButtonTab, Tabs } from '@/components/navigation/NavigationTabs';
import { useActiveTab } from '@/components/navigation/NavigationTabs/useActiveTab';
import { PageHeader } from '@/components/navigation/PageHeader';
import { DataTable_LEGACYCard } from '@/components/tables/DataTable_LEGACY/DataTable_LEGACYCard';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { useMarkPresentationApprovalsViewed } from '@/modules/presentationApprovals/PresentationApprovalBadge/useMarkPresentationApprovalsViewed';
import { PresentationApprovalRequestsTable } from '@/modules/presentationApprovals/PresentationApprovalRequestsTable/PresentationApprovalRequestsTable';
import {
  TasksTable,
  TaskTableColumns,
} from '@/modules/tasks/components/TasksTable/TasksTable';
import { usePresentationApprovalWorkflowEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/usePresentationApprovalWorkflowEnabled';
import { ROUTE_KEYS } from '@/navigation/constants';
import { EntityTaskWhereInput } from '@/types/schema';

enum TasksPageTab {
  PresentationApprovals = 'presentation-approvals',
  Tasks = 'tasks',
}

export function TasksPage() {
  const taskTableFilter: EntityTaskWhereInput = {};
  const isApprovalWorkflowEnabled = usePresentationApprovalWorkflowEnabled();
  const canApprovePresentations = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_APPROVE_PRESENTATIONS
  );
  // The approvals tab lists approval requests for the viewer, so only users
  // who can approve presentations see it.
  const showPresentationApprovals =
    isApprovalWorkflowEnabled && canApprovePresentations;
  const { activeTab: requestedTab, setActiveTab } = useActiveTab<TasksPageTab>({
    defaultTab: TasksPageTab.PresentationApprovals,
  });
  // Without the approval workflow, the page has only the tasks list, so a
  // link to the approvals tab also opens the tasks list. An unknown tab
  // parameter also opens the tasks list.
  const isKnownTab = Object.values(TasksPageTab).includes(requestedTab);
  const activeTab =
    showPresentationApprovals && isKnownTab ? requestedTab : TasksPageTab.Tasks;
  useMarkPresentationApprovalsViewed(
    activeTab === TasksPageTab.PresentationApprovals
  );

  return (
    <Stack>
      <PageHeader heading="Tasks" />
      <Stack p={2} spacing={2} sx={{ width: '100%' }}>
        {showPresentationApprovals && (
          <Tabs>
            <ButtonTab
              display="Presentation approvals"
              isActive={activeTab === TasksPageTab.PresentationApprovals}
              onClick={() => setActiveTab(TasksPageTab.PresentationApprovals)}
            />
            <ButtonTab
              display="Tasks"
              isActive={activeTab === TasksPageTab.Tasks}
              onClick={() => setActiveTab(TasksPageTab.Tasks)}
            />
          </Tabs>
        )}
        {activeTab === TasksPageTab.PresentationApprovals && (
          <PresentationApprovalRequestsTable />
        )}
        {activeTab === TasksPageTab.Tasks && (
          <DataTable_LEGACYCard>
            <TasksTable
              filter={taskTableFilter}
              columns={[
                TaskTableColumns.DUE_AT,
                TaskTableColumns.SUB_BRAND,
                TaskTableColumns.TASK_NAME,
                TaskTableColumns.ASSIGNEE,
                TaskTableColumns.ENTITY_NAME,
                TaskTableColumns.CLIENT_NAME,
              ]}
              from={ROUTE_KEYS.TASKS}
            />
          </DataTable_LEGACYCard>
        )}
      </Stack>
    </Stack>
  );
}
